import React, { type ComponentType, useCallback, useMemo, useRef } from 'react';
import { type FieldError } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel, type SxProps, type Theme, useTheme } from '@mui/material';
import _omit from 'lodash/omit';
import { mergeSx } from 'merge-sx';

import RequiredMark from '@/components/fields/RequiredMark';
import typography from '@/theme/typography';

const PROPS_TO_OMIT = ['withFormControl', 'field'];

interface FormControlProps<EventType, ValueType> {
  withFormControl?: boolean;
  isDisabled?: boolean;
  disabled?: boolean;
  name?: string;
  label?: string;
  value?: ValueType;
  error?: string | FieldError;
  onChange?: (event: EventType) => void;
  field?: {
    value: ValueType;
    name: string;
    onChange: (event: EventType) => void;
  };
  outerSx?: SxProps<Theme>;
  markAsRequired?: boolean;
}

const withFormControl = <EventType, ValueType, FieldProps extends FormControlProps<EventType, ValueType>>(
  Field: ComponentType<FieldProps>
) => {
  const ComponentWithFormControl: React.FC<FieldProps & FormControlProps<EventType, ValueType>> = props => {
    const fieldRef = useRef<(ComponentType<FieldProps> & { focus: () => void }) | null>();
    const { spacing, palette } = useTheme();
    const { name, label, field: fieldProp, withFormControl, outerSx } = props;
    const isDisabled = props.isDisabled || props.disabled;
    const uniqueId = useMemo(() => `${name}-${crypto.randomUUID()}`, [name]);
    const fieldProps = useMemo(() => _omit(props, PROPS_TO_OMIT) as FieldProps, [props]);
    const error = useMemo(
      () => (typeof fieldProps.error === 'string' ? fieldProps.error : fieldProps.error?.message),
      [fieldProps]
    );

    const onLabelClick = useCallback(() => !isDisabled && fieldRef.current?.focus(), [isDisabled]);

    const field = useMemo(
      () => <Field {...fieldProps} {...fieldProp} error={error} id={uniqueId} ref={fieldRef} />,
      [fieldProps, fieldProp, uniqueId, error]
    );

    if (!withFormControl) {
      return field;
    }

    return (
      <FormControl sx={mergeSx({ p: 3 }, outerSx)} error={!!error}>
        <FormLabel
          htmlFor={uniqueId}
          onClick={onLabelClick}
          sx={{
            ...typography.body3,
            mb: 1,
            fontWeight: typography.fontWeightMedium,
            lineHeight: spacing(2),
            color: `${palette.grey[700]} !important`
          }}
        >
          {label}
          {fieldProps.markAsRequired && <RequiredMark />}
        </FormLabel>
        {field}
        {error && <FormHelperText sx={{ mx: 0 }}>{error}</FormHelperText>}
      </FormControl>
    );
  };

  return ComponentWithFormControl;
};

export default withFormControl;
